import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetQRCode } from '@/hooks/useQRCode/useGetQRCode'
import { useGetQRCodeStatus } from '@/hooks/useQRCode/useGetQRCodeStatus'
import { useDownloadQRCode } from '@/hooks/useQRCode/useDownloadQRCode'
import { useDownloadAllQRCodes } from '@/hooks/useQRCode/useDownloadAllQRCodes'
import { useRegenerateQRCodes } from '@/hooks/useQRCode/useRegenerateQRCodes'
import { useRegenerateTrolleyQRCode } from '@/hooks/useQRCode/useRegenerateTrolleyQRCode'
import { useRegenerateErrorQRCodes } from '@/hooks/useQRCode/useRegenerateErrorQRCodes'
import { useCheckRegenerateErrors } from '@/hooks/useQRCode/useCheckRegenerateErrors'
import { useLoading } from '@/contexts/LoadingContext'
import { useToast } from '@/hooks/useToast'

const ITEMS_PER_PAGE = 12

export function useQRCodePage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchText, setSearchText] = useState('')
  const [searchQuery, setSearchQuery] = useState<Nillable<string>>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const {
    items: qrCodes,
    total_pages,
    isLoading: isLoadingQRCodes,
    refetch: refetchQRCodes,
  } = useGetQRCode({
    page: currentPage,
    page_size: ITEMS_PER_PAGE,
    search: searchQuery,
  })
  const {
    status,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useGetQRCodeStatus()
  const { hasErrorCodes, refetch: refetchErrorCheck } =
    useCheckRegenerateErrors()
  const { download, isLoading: isDownloading } = useDownloadQRCode()
  const { downloadAll, isLoading: isDownloadingAll } = useDownloadAllQRCodes()
  const { showLoading, hideLoading } = useLoading()
  const {
    regenerate,
    startPollingByProcessId,
    isLoading: isRegenerating,
  } = useRegenerateQRCodes({
    onInProgress: () => {
      setIsModalOpen(false)
      showLoading(t('common.processing'))
    },
    onDone: async () => {
      hideLoading()
      await refetchQRCodes()
      await refetchStatus()
      await refetchErrorCheck()
      toast({
        title: t('common.reloadSuccess'),
        variant: 'success',
      })
    },
  })

  const { regenerateTrolleyQRCode } = useRegenerateTrolleyQRCode()
  const { regenerateErrorQRCodes, isRegeneratingErrors } =
    useRegenerateErrorQRCodes({
      onInProgress: () => {
        showLoading(t('common.processing'))
      },
      onDone: async () => {
        hideLoading()
        await refetchQRCodes()
        await refetchErrorCheck()
        toast({
          title: t('common.reloadSuccess'),
          variant: 'success',
        })
      },
    })

  useEffect(() => {
    const shouldShowLoading =
      (isLoadingQRCodes || isLoadingStatus) && searchText.trim().length === 0

    if (shouldShowLoading) {
      showLoading(t('common.loading'))
    } else {
      hideLoading()
    }
  }, [
    isLoadingQRCodes,
    isLoadingStatus,
    showLoading,
    hideLoading,
    t,
    searchText,
  ])

  useEffect(() => {
    if (status?.needs_regeneration && !isLoadingStatus && !isLoadingQRCodes) {
      setIsModalOpen(true)
    } else if (status?.needs_regeneration === false) {
      setIsModalOpen(false)
    }
  }, [status?.needs_regeneration, isLoadingStatus, isLoadingQRCodes])

  useEffect(() => {
    if (
      status?.process_id &&
      !isLoadingStatus &&
      !isRegenerating &&
      !isLoadingQRCodes
    ) {
      showLoading(t('common.processing'))
      startPollingByProcessId(status.process_id)
    } else if (
      !status?.process_id &&
      !isLoadingStatus &&
      !isRegenerating &&
      !isLoadingQRCodes
    ) {
      hideLoading()
    }
  }, [
    status?.process_id,
    isLoadingStatus,
    isRegenerating,
    isLoadingQRCodes,
    startPollingByProcessId,
    showLoading,
    hideLoading,
    t,
  ])

  useEffect(() => {
    if (currentPage > total_pages && total_pages > 0) {
      setCurrentPage(1)
    }
  }, [total_pages, currentPage])

  const handleSearchChange = (value: string) => {
    setSearchText(value)

    const trimmed = value.trim()
    if (trimmed.length === 0) {
      if (searchQuery !== null) {
        setSearchQuery(null)
        setCurrentPage(1)
      }
      return
    }

    if (trimmed.length < 3) {
      if (searchQuery !== null) {
        setSearchQuery(null)
        setCurrentPage(1)
      }
      return
    }

    if (trimmed !== searchQuery) {
      setSearchQuery(trimmed)
      setCurrentPage(1)
    }
  }

  const handleDownload = async (trolleyId: number, trolleyName: string) => {
    try {
      await download(trolleyId, `qr-code-${trolleyName}.png`)
      toast({
        title: t('common.downloadSuccess'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Error downloading QR code:', error)
    }
  }

  const handleDownloadAll = async () => {
    try {
      await downloadAll('all-qr-codes.zip')
      await refetchQRCodes()
      toast({
        title: t('common.downloadSuccess'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Error downloading all QR codes:', error)
    }
  }

  const handleReload = async () => {
    try {
      await regenerate()
    } catch (error) {
      console.error('Error reloading QR codes:', error)
      hideLoading()
    }
  }

  const handleRegenerateTrolley = async (trolleyId: number) => {
    try {
      showLoading(t('common.processing'))
      await regenerateTrolleyQRCode(trolleyId)
      await refetchQRCodes()
      toast({
        title: t('common.reloadSuccess'),
        variant: 'success',
      })
    } catch (error) {
      console.error('Error regenerating trolley QR code:', error)
    } finally {
      hideLoading()
    }
  }

  const handleRegenerateErrors = async () => {
    try {
      await regenerateErrorQRCodes()
    } catch (error) {
      console.error('Error regenerating error QR codes:', error)
      hideLoading()
    }
  }

  return {
    searchTerm: searchText,
    setSearchTerm: handleSearchChange,
    isModalOpen,
    setIsModalOpen,
    currentPage,
    setCurrentPage,
    qrCodes,
    totalPages: total_pages,
    status,
    isDownloading,
    isDownloadingAll,
    isRegenerating,
    handleDownload,
    handleDownloadAll,
    handleReload,
    handleRegenerateTrolley,
    handleRegenerateErrors,
    isRegeneratingErrors,
    hasErrorCodes,
  }
}
